/**
 * The library's accepted formats and size caps.
 *
 * Deliberately free of any Firebase or API-client import: these values are a
 * contract with core, they are useful to reason about on their own, and a test
 * that pins them should not have to stand up an auth SDK to do it.
 */

/** Per-file cap. Mirrors `MAX_UPLOAD_BYTES` in core's `library/application/storage.py`. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Per-organization cap. Mirrors `MAX_ORG_LIBRARY_BYTES` in the same module. */
export const MAX_ORG_LIBRARY_BYTES = 200 * 1024 * 1024;

/**
 * The accepted formats, keyed by extension.
 *
 * Keyed by extension rather than by MIME because **the browser's own
 * `File.type` is not trustworthy for these**: `.md` arrives as `""` or
 * `application/octet-stream` far more often than as `text/markdown`. Core
 * validates the (extension, content_type) *pair*, so deriving the type from
 * the name here is what makes the two agree.
 *
 * `svg` is deliberately absent: it is markup rather than raster, so the
 * agent's vision path cannot read it without rendering, and it can carry
 * scripts that would run in a member's browser.
 */
export const ACCEPTED_EXTENSIONS: Record<string, string> = {
  ".md": "text/markdown",
  ".txt": "text/plain",
  ".pdf": "application/pdf",
  ".pptx":
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
};

/** For the file input's `accept` attribute. */
export const ACCEPT_ATTRIBUTE = Object.keys(ACCEPTED_EXTENSIONS).join(",");

export const contentTypeForFilename = (filename: string): string | null => {
  const dot = filename.lastIndexOf(".");
  if (dot < 0) return null;
  return ACCEPTED_EXTENSIONS[filename.slice(dot).toLowerCase()] ?? null;
};
